import type { ReactNode } from 'react'
import { skillPoolLabel } from '../labels'
import { aspectChips, type CharacterSheet } from '../types'
import {
  AbilitiesBlock,
  AspectsBlock,
  ConsequencesBlock,
  FateBlock,
  GearBlock,
  LadderBlock,
  SkillsBlock,
  StressBlock,
} from './PrintBlocks'
import { PrintSpellcasting } from './PrintSpellcasting'
import {
  CAPACITY,
  blankSections,
  printedShapes,
  shapeName,
  sheetVariant,
  signed,
  variantTitle,
  type ShapeSheet,
} from './printModel'
import styles from './print.module.css'

/**
 * The printed character sheet, modelled on the official Dresden Files RPG sheets.
 *
 * **The page count is fixed**: one sheet for a character, two for a spellcaster. That is the
 * whole reason the blocks have capacities rather than growing to fit — a sheet whose length
 * depends on how many stunts a character happens to own prints a second, near-empty page and
 * nobody notices until they are standing at the printer.
 *
 * How it differs from the official sheets, and why:
 *
 * - **A4, not US Letter** — 210×297 rather than 216×279, so the layout is a reflow of the
 *   originals rather than a copy of them.
 * - **Stress runs to ten circles, not eight.** The ones past the character's current track
 *   print dashed, so an Endurance that goes up mid-campaign is a pencil mark rather than a
 *   reprint. Marked stress gets a dot rather than a cross — the cross belongs to the player.
 * - **Hunger prints inside the stress box**, with the rule that governs it on the line
 *   beneath, so a shapeshifting vampire is not stuck between two official sheets that each
 *   leave half of them out.
 * - **There is a Gear &amp; Items box**, which no official sheet has.
 */
export function PrintSheet({
  sheet,
  otherShapes,
  blank = false,
}: {
  /** The sheet as loaded — for a shapeshifter, whichever shape the server scoped it to. */
  sheet: CharacterSheet
  /** The other printed shape, when there is one. At most one: the sheet prints two. */
  otherShapes: ShapeSheet[]
  /**
   * Print the form and none of the writing.
   *
   * Every box, rung, row and circle stays; the character's entries do not. It is still
   * *this* character's form — two pages if they cast, a Hunger row if they feed, two skill
   * boxes if they change shape — which is what makes it useful as a spare at the table.
   */
  blank?: boolean
}) {
  const sections = blank ? blankSections(sheet.sections) : sheet.sections
  const core = sections.core
  const shapes = printedShapes(sections.shapes?.shapes ?? [])
  const variant = sheetVariant(core, shapes, sections.stress)
  const name = core?.name ?? 'Character'

  const primaryShape = shapes.find((shape) => shape.id === sheet.shapeId)
  const second = otherShapes[0]

  // Both belong to the character's build rather than to the form, so a blank sheet states
  // neither — and neither can be read back off the blanked sections, which carry the
  // standard five rungs rather than this character's cap.
  const pool = sheet.sections.skills?.poolOption
  const poolLabel = blank || pool === undefined || pool === null ? '' : skillPoolLabel(String(pool))
  const cap = sheet.sections.skills?.maxLevelValue
  const skillCap = blank || cap === undefined ? '' : signed(cap)

  // A shapeshifter prints two skill boxes, exactly as the official Shapeshifter sheet does,
  // and the powers box gives up rows to make room for the second one.
  const isShifter = second !== undefined
  const abilityCapacity = isShifter ? CAPACITY.abilitiesWithTwoShapes : CAPACITY.abilities
  const gearCapacity = isShifter ? CAPACITY.gearWithTwoShapes : CAPACITY.gear

  const skillsTitle = (shape: typeof primaryShape) =>
    shape === undefined ? 'Skills' : `Skills (${shapeName(shape)})`

  return (
    <>
      {/* Every official sheet — plain, hunger and shapeshifter alike — is titled "Character
          Sheet"; the variant only ever changed what was printed inside. It survives here as
          the page's accessible name, which is what tests and screen readers read. */}
      <Page name={name} title="Character Sheet" label={variantTitle(variant)} blank={blank}>
        <div className={styles.columns}>
          <div className={styles.column}>
            <AspectsBlock
              highConcept={sections.aspects?.highConcept ?? ''}
              trouble={sections.aspects?.trouble ?? ''}
              others={aspectChips(sections.aspects)}
            />
            <StressBlock stress={sections.stress} />
            <ConsequencesBlock consequences={sections.consequences} />
            <FateBlock
              fate={sections.fate}
              skills={sections.skills}
              poolLabel={poolLabel}
              skillCap={skillCap}
              grow
            />
          </div>

          <div className={styles.column}>
            <SkillsBlock skills={sections.skills} title={skillsTitle(primaryShape)} />
            {second !== undefined && (
              <SkillsBlock
                skills={
                  blank ? blankSections(second.sheet.sections).skills : second.sheet.sections.skills
                }
                title={`Skills (${shapeName(second.shape)})`}
              />
            )}
            <AbilitiesBlock
              abilities={sections.abilities}
              shapeId={sheet.shapeId}
              capacity={abilityCapacity}
              grow
            />
            <GearBlock equipment={sections.equipment} capacity={gearCapacity} />
            <LadderBlock />
          </div>
        </div>
      </Page>

      {core?.hasSpellcasting === true && (
        <Page name={name} title="Spellcasting Sheet" label="Spellcasting" blank={blank}>
          <PrintSpellcasting
            skills={sections.skills}
            specializations={sections.specializations}
            focusItems={sections.focusItems}
            enchantedItems={sections.enchantedItems}
            roteSpells={sections.roteSpells}
            spellcasting={core.spellcasting}
          />
        </Page>
      )}
    </>
  )
}

/**
 * One sheet of A4, headed the way the official sheets are: the game's name over the sheet's
 * name, then the character and player bars.
 *
 * The player bar prints blank on purpose. The app has no idea who is holding the sheet, and
 * a table needs somewhere to write it.
 */
function Page({
  name,
  title,
  label,
  blank,
  children,
}: {
  name: string
  title: string
  /** The page's accessible name — which variant of the sheet this is. */
  label: string
  /** Leave the name bar and the footer empty; the accessible name keeps it either way. */
  blank: boolean
  children: ReactNode
}) {
  const printedName = blank ? '' : name
  return (
    <article className={`${styles.doc} ${styles.page}`} aria-label={`${name} — ${label}`}>
      <header className={styles.masthead}>
        <p className={styles.kicker}>The Dresden Files Role-Playing Game</p>
        <p className={styles.sheetTitle}>{title}</p>
      </header>

      <div className={styles.nameBars}>
        {/* The character's name is the page's heading — the sheet is a document about them,
            and the masthead above says only which game and which sheet. */}
        <div className={styles.nameBar}>
          <span className={styles.nameBarLabel}>Character</span>
          <h1 className={styles.nameBarValue}>{printedName}</h1>
        </div>
        <div className={`${styles.nameBar} ${styles.nameBarRight}`}>
          <span className={styles.nameBarLabel}>Player</span>
          <span className={styles.nameBarValue} />
        </div>
      </div>

      {children}

      <footer className={styles.footer}>
        <span>{printedName}</span>
        <span>Harrowgate</span>
      </footer>
    </article>
  )
}
